import React, { createContext, useEffect, useReducer } from "react";
import authService from "../services/authService";
import { ISession, User } from "../declarations/auth";
import axios from "../services/AxiosInstance";
import {
  getSession,
  sendMessageToExtension,
  setSession,
} from "../services/util";
import useSnackbar from "../hooks/useSnackbar";

interface AuthState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  user: User | null;
}

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

/**
 * Method used to validate access token
 *
 * @param accessToken Token to be validated
 */
const isValidToken = (accessToken: any) => {
  //TODO: Validate token here
  console.log(accessToken);
  return true;
};

export const sendTokensToChromeExtension = (
  tokens: {
    accessToken: string;
    refreshToken: string;
  } | null
) => {
  if (tokens) {
    sendMessageToExtension({
      action: "LOGIN",
      payload: tokens,
    });
  } else {
    sendMessageToExtension({
      action: "LOGOUT",
    });
  }
};

const reducer = (state: AuthState, action: any): AuthState => {
  switch (action.type) {
    case "INITIALIZE": {
      const { isAuthenticated, user } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialized: true,
        user,
      };
    }
    case "LOGIN": {
      const { user } = action.payload;
      return {
        ...state,
        isAuthenticated: true,
        user,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
    }
    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialAuthState,
  login: async (code: string) => {
    console.log(code);
  },
  logout: async () => {
    console.log("logout");
  },
});

export const AuthProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const { notify, hideNotification } = useSnackbar();

  const updateSession = (session: ISession | null) => {
    setSession(session);
    try {
      sendTokensToChromeExtension(session);
    } catch (error) {
      notify({
        message: (error as Error).toString(),
        onClose: hideNotification,
        type: "FAILURE",
        autoHideDuration: 5000,
      });
    }
  };

  const login = async (code: string) => {
    try {
      const response = await authService.loginWithGoogle(code);
      const session: ISession = {
        refreshToken: response.refresh_token,
        accessToken: response.access_token,
        user: response.user,
      };
      updateSession(session);
      dispatch({
        type: "LOGIN",
        payload: {
          user: session.user,
        },
      });
    } catch (error) {
      notify({
        message:
          (error as any)?.response?.data?.message ?? "Something went wrong!",
        onClose: hideNotification,
        type: "FAILURE",
      });
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: "LOGOUT" });
    } catch (error) {
      notify({
        message:
          (error as any)?.response?.data?.message ?? "Something went wrong!",
        onClose: hideNotification,
        type: "FAILURE",
      });
    } finally {
      updateSession(null);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const session = getSession();

        if (session && isValidToken(session)) {
          updateSession(session);
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: true,
              user: session.user,
            },
          });
        } else {
          dispatch({
            type: "INITIALIZE",
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: "INITIALIZE",
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  if (!state.isInitialized) {
    return <></>;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
